import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import Sidebar from "../../components/Sidebar";

// Features we're teasing on the coming-soon card. Kept here so the copy is easy
// to tweak without touching layout.
const FEATURES = [
  "Stock and batch tracking",
  "Dispense against prescriptions",
  "Low-stock and expiry alerts",
];

/**
 * Placeholder landing page for the not-yet-built Pharmacy module. Reachable from
 * the "Pharmacy" sidebar item. The "Notify me" button only fires a local
 * confirmation toast for now — persisting the interest signal to the backend is
 * a follow-up (no notify-interest endpoint exists yet).
 */
const PharmacyComingSoonPage: React.FC = () => {
  const navigate = useNavigate();
  const [notified, setNotified] = useState(false);

  const handleNotify = () => {
    if (notified) return;
    setNotified(true);
    // TODO(pharmacy): POST the signed-in user's interest to the backend once the
    // notify-interest endpoint lands. Until then this is a client-only ack.
    toast.success("Thanks! We'll notify you when the Pharmacy dashboard is ready.");
  };

  return (
    <div className="min-h-screen bg-[#f5f5f7] flex">
      <Sidebar />

      <div className="flex-1 min-w-0 flex flex-col pl-0 lg:pl-[var(--sidebar-w)] transition-[padding-left] duration-300 ease-in-out pt-14 lg:pt-0">
        <div className="flex-1 flex items-center justify-center p-4 sm:p-6 lg:p-8">
          <div className="w-full max-w-lg bg-white rounded-3xl border border-slate-100 shadow-sm px-8 py-10 flex flex-col items-center text-center">
            {/* Icon tile */}
            <div className="h-16 w-16 rounded-2xl bg-gradient-to-br from-cyan-400 to-blue-500 flex items-center justify-center text-white shadow-md shadow-blue-500/20">
              <svg className="w-8 h-8" fill="none" stroke="currentColor" strokeWidth={1.75} strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
                <path d="m10.5 20.5 10-10a4.95 4.95 0 1 0-7-7l-10 10a4.95 4.95 0 1 0 7 7Z" />
                <path d="m8.5 8.5 7 7" />
              </svg>
            </div>

            {/* Coming soon pill */}
            <span className="mt-5 inline-flex items-center gap-1.5 rounded-full bg-sky-50 px-3 py-1 text-xs font-semibold text-sky-600">
              <span className="h-1.5 w-1.5 rounded-full bg-sky-500" />
              Coming soon
            </span>

            <h1 className="mt-4 text-2xl font-bold text-slate-900">Pharmacy dashboard</h1>
            <p className="mt-3 max-w-md text-sm leading-relaxed text-slate-500">
              We are building inventory, dispensing, and stock tracking so you can run your
              clinic pharmacy inside Kaagazy. It will show up here once it is ready.
            </p>

            {/* Feature checklist */}
            <ul className="mt-6 flex flex-col gap-2.5">
              {FEATURES.map((feature) => (
                <li key={feature} className="flex items-center gap-2.5 text-sm text-slate-700">
                  <svg className="w-4 h-4 flex-shrink-0 text-teal-600" fill="none" stroke="currentColor" strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                  {feature}
                </li>
              ))}
            </ul>

            {/* Actions */}
            <div className="mt-8 flex w-full flex-col gap-3 sm:flex-row sm:justify-center">
              <button
                onClick={handleNotify}
                disabled={notified}
                className={`inline-flex items-center justify-center gap-2 rounded-xl px-5 py-3 text-sm font-semibold text-white transition-colors ${
                  notified
                    ? "bg-slate-400 cursor-default"
                    : "bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-600 hover:to-blue-700"
                }`}
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
                  <path d="M6 8a6 6 0 0 1 12 0c0 7 3 9 3 9H3s3-2 3-9" />
                  <path d="M10.3 21a1.94 1.94 0 0 0 3.4 0" />
                </svg>
                {notified ? "You're on the list" : "Notify me when it is ready"}
              </button>
              <button
                onClick={() => navigate("/dashboard")}
                className="inline-flex items-center justify-center rounded-xl border border-slate-200 px-5 py-3 text-sm font-semibold text-slate-700 hover:bg-slate-50 transition-colors"
              >
                Back to dashboard
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PharmacyComingSoonPage;
